/**
 * A simple event service for handling post-related events across the application
 */

// Define event types to ensure consistency across the app
export enum PostEventType {
  POST_DELETED = 'post_deleted',
  POST_CREATED = 'post_created',
  POST_UPDATED = 'post_updated',
  POST_LIKED = 'post_liked',
  POST_UNLIKED = 'post_unliked',
}

// Define event data types
export interface PostDeletedEvent {
  postId: number;
}

class PostEventService {
  private listeners: { [key: string]: Function[] } = {};

  /**
   * Add a listener for a specific event
   * @param event The event name
   * @param callback The callback function
   */
  addEventListener(event: string, callback: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  /**
   * Remove a listener for a specific event
   * @param event The event name
   * @param callback The callback function
   */
  removeEventListener(event: string, callback: Function) {
    if (!this.listeners[event]) {
      return;
    }
    this.listeners[event] = this.listeners[event].filter(
      (listener) => listener !== callback
    );
  }

  /**
   * Emit an event with data
   * @param event The event name
   * @param data The data to pass to listeners
   */
  emit(event: string, data: any) {
    if (!this.listeners[event]) {
      return;
    }
    this.listeners[event].forEach((listener) => {
      listener(data);
    });
  }
  
  /**
   * Emit a post deleted event
   * @param postId The ID of the deleted post
   */
  emitPostDeleted(postId: number) {
    this.emit(PostEventType.POST_DELETED, { postId });
  }
}

// Export a singleton instance
export const postEventService = new PostEventService();
